import { db } from '../firebase';
// Import getDocFromServer and other necessary Firestore functions
import { collection, query, where, getDocs, orderBy, Timestamp, limit, getDoc, doc, getDocFromServer } from 'firebase/firestore';
import { reportCacheUtil } from '../helpers/reportCacheUtil';
// Import date-fns functions
import { subMonths, startOfMonth, parse, endOfMonth, isValid, parseISO } from 'date-fns';
// Import getAuth from firebase/auth
import { getAuth } from 'firebase/auth';
// Import invoiceService to delegate actions
import { invoiceService } from './invoiceService';

// Define a TTL for the cache (e.g., 15 minutes in milliseconds)
const CACHE_TTL = 15 * 60 * 1000;

class ReportService {
  constructor() {
    // No need for this.invoiceCache anymore
  }

  // --- Fetching Single Invoice Data (getInvoiceData - DEPRECATED? Keep for now if used elsewhere) ---
  async getInvoiceData(userId, invoiceId) {
    if (!userId || !invoiceId) {
      console.error("ReportService: getInvoiceData requires userId and invoiceId.");
      return null;
    }

    const cacheKey = `invoice_${userId}_${invoiceId}`;
    // Try fetching from cache first using reportCacheUtil
    const cachedData = reportCacheUtil.getFromCache(cacheKey);
    if (cachedData) {
      //console.log(`ReportService: Returning cached data for invoice ${invoiceId}`);
      // Ensure dates are Date objects after parsing from JSON
      if (cachedData.createdAt?.seconds) cachedData.createdAt = new Date(cachedData.createdAt.seconds * 1000);
      if (cachedData.date?.seconds) cachedData.date = new Date(cachedData.date.seconds * 1000);
      // Add similar checks for other date fields like summaryData[*].date
      if (cachedData.summaryData && Array.isArray(cachedData.summaryData)) {
         cachedData.summaryData = cachedData.summaryData.map(entry => {
            if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
            return entry;
         });
      }
      return cachedData;
    }

    //console.log(`ReportService: Fetching invoice ${invoiceId} from Firestore.`);
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      // Use getDocFromServer to bypass Firestore cache if needed, or getDoc for standard fetch
      // const invoiceSnap = await getDocFromServer(invoiceRef); // Use if bypassing Firestore cache is critical
      const invoiceSnap = await getDoc(invoiceRef); // Standard fetch

      if (invoiceSnap.exists()) {
        const invoiceData = { id: invoiceSnap.id, ...invoiceSnap.data() };
        // Verify ownership (optional but recommended)
        if (invoiceData.userId !== userId) {
          console.warn(`ReportService: User ${userId} attempted to fetch invoice ${invoiceId} belonging to ${invoiceData.userId}.`);
          return null; // Or throw an error
        }
        // Save to cache using reportCacheUtil before returning
        reportCacheUtil.saveToCache(cacheKey, invoiceData, CACHE_TTL);
        // Ensure dates are Date objects before returning
        if (invoiceData.createdAt?.seconds) invoiceData.createdAt = new Date(invoiceData.createdAt.seconds * 1000);
        if (invoiceData.date?.seconds) invoiceData.date = new Date(invoiceData.date.seconds * 1000);
        // Add similar checks for other date fields
        if (invoiceData.summaryData && Array.isArray(invoiceData.summaryData)) {
           invoiceData.summaryData = invoiceData.summaryData.map(entry => {
              if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
              return entry;
           });
        }
        return invoiceData;
      } else {
        //console.log(`ReportService: Invoice ${invoiceId} not found.`);
        return null;
      }
    } catch (error) {
      console.error(`ReportService: Error fetching invoice ${invoiceId}:`, error);
      throw error; // Re-throw the error for handling upstream
    }
  }

  // --- NEW/RESTORED: Fetching Single Invoice Data by ID (Handles Caching & Options) ---
  async getInvoiceById(userId, invoiceId, options = {}) {
    if (!userId || !invoiceId) {
      console.error("ReportService: getInvoiceById requires userId and invoiceId.");
      return null;
    }

    const cacheKey = `invoice_${userId}_${invoiceId}`;
    // Determine if a server fetch is forced by options
    const forceServerFetch = options?.source === 'server' || options?.forceRefresh === true;

    // Try fetching from cache first unless a server fetch is forced
    if (!forceServerFetch) {
      const cachedData = reportCacheUtil.getFromCache(cacheKey);
      if (cachedData) {
        //console.log(`[ReportService][getInvoiceById] Returning cached data for invoice ${invoiceId}`);
        // Ensure dates are Date objects after parsing from JSON
        // *** FIX START ***
        if (cachedData.createdAt?.seconds) {
          cachedData.createdAt = new Date(cachedData.createdAt.seconds * 1000);
        } else if (typeof cachedData.createdAt === 'string') {
          // Handle ISO string date from cache
          try {
            const parsedDate = parseISO(cachedData.createdAt); // Use date-fns for robust parsing
            if (isValid(parsedDate)) {
              cachedData.createdAt = parsedDate;
            } else {
              console.warn(`[ReportService][getInvoiceById] Cached createdAt string is invalid: ${cachedData.createdAt}`);
              cachedData.createdAt = null; // Or handle as appropriate
            }
          } catch (e) {
            console.error(`[ReportService][getInvoiceById] Error parsing cached createdAt string: ${cachedData.createdAt}`, e);
            cachedData.createdAt = null;
          }
        }
        // *** FIX END ***

        if (cachedData.paidDate?.seconds) cachedData.paidDate = new Date(cachedData.paidDate.seconds * 1000);
        else if (cachedData.paidDate && typeof cachedData.paidDate === 'string') cachedData.paidDate = new Date(cachedData.paidDate); // Handle ISO string from cache
        // Add similar checks for other date fields like summaryData[*].date
        if (cachedData.summaryData && Array.isArray(cachedData.summaryData)) {
           cachedData.summaryData = cachedData.summaryData.map(entry => {
              if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
              else if (entry.date && typeof entry.date === 'string') entry.date = new Date(entry.date); // Handle ISO string
              return entry;
           });
        }
        return cachedData;
      }
    } else {
       //console.log(`[ReportService][getInvoiceById] Force server fetch requested for invoice ${invoiceId}`);
    }

    //console.log(`[ReportService][getInvoiceById] Fetching invoice ${invoiceId} from Firestore.`);
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      // Use getDocFromServer if explicitly requested, otherwise standard getDoc
      const invoiceSnap = forceServerFetch ? await getDocFromServer(invoiceRef) : await getDoc(invoiceRef);

      if (invoiceSnap.exists()) {
        const invoiceData = { id: invoiceSnap.id, ...invoiceSnap.data() };
        // Verify ownership
        if (invoiceData.userId !== userId) {
          console.warn(`[ReportService][getInvoiceById] User ${userId} attempted to fetch invoice ${invoiceId} belonging to ${invoiceData.userId}.`);
          return null;
        }

        // Convert Firestore Timestamps to JS Dates before caching and returning
        if (invoiceData.createdAt?.seconds) invoiceData.createdAt = new Date(invoiceData.createdAt.seconds * 1000);
        if (invoiceData.paidDate?.seconds) invoiceData.paidDate = new Date(invoiceData.paidDate.seconds * 1000);
        if (invoiceData.deletedAt?.seconds) invoiceData.deletedAt = new Date(invoiceData.deletedAt.seconds * 1000);
        // Convert summaryData dates
        if (invoiceData.summaryData && Array.isArray(invoiceData.summaryData)) {
           invoiceData.summaryData = invoiceData.summaryData.map(entry => {
              if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
              return entry;
           });
        }

        // Save to cache using reportCacheUtil before returning
        reportCacheUtil.saveToCache(cacheKey, invoiceData, CACHE_TTL);
        //console.log(`[ReportService][getInvoiceById] Fetched and cached invoice ${invoiceId}`);
        return invoiceData; // Return data with JS Dates

      } else {
        //console.log(`[ReportService][getInvoiceById] Invoice ${invoiceId} not found.`);
        // Cache the 'not found' result to prevent repeated Firestore reads for non-existent IDs
        reportCacheUtil.saveToCache(cacheKey, null, CACHE_TTL);
        return null;
      }
    } catch (error) {
      console.error(`[ReportService][getInvoiceById] Error fetching invoice ${invoiceId}:`, error);
      throw error; // Re-throw the error for handling upstream
    }
  }

  // --- Fetching All Invoice Data (with Caching) ---
  async getAllInvoiceData(userId, practiceId = 'all', startDate = null, endDate = null, options = {}) { // Added endDate param
    if (!userId) {
      console.error("ReportService: getAllInvoiceData requires userId.");
      return [];
    }

    // Construct a cache key based on parameters including endDate
    const cacheKey = `allInvoices_${userId}_${practiceId}_${startDate ? startDate.toISOString() : 'null'}_${endDate ? endDate.toISOString() : 'null'}`;
    const forceRefresh = options?.forceRefresh || false; // Check for forceRefresh option

    // Try fetching from cache first unless forceRefresh is true
    if (!forceRefresh) {
      const cachedData = reportCacheUtil.getFromCache(cacheKey);
      if (cachedData) {
        //console.log(`ReportService: Returning cached invoice data for key: ${cacheKey}`);
        // Ensure dates are Date objects after parsing from JSON
        return cachedData.map(inv => {
           if (inv.createdAt?.seconds) inv.createdAt = new Date(inv.createdAt.seconds * 1000);
           // Add other date conversions as needed (e.g., summaryData dates)
           if (inv.summaryData && Array.isArray(inv.summaryData)) {
              inv.summaryData = inv.summaryData.map(entry => {
                 if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
                 return entry;
              });
           }
           return inv;
        });
      }
    } else {
       //console.log(`ReportService: Force refresh requested for key: ${cacheKey}`);
    }


    //console.log(`ReportService: Fetching all invoices from Firestore for key: ${cacheKey}`);
    try {
      const invoicesRef = collection(db, 'invoices');
      let q = query(invoicesRef, where('userId', '==', userId));

      if (practiceId !== 'all') {
        q = query(q, where('practiceId', '==', practiceId));
      }

      // Add date filtering if startDate is provided
      if (startDate instanceof Date && isValid(startDate)) {
       //  console.log(`ReportService: Applying start date filter >= ${startDate.toISOString()}`);
         // Assuming 'createdAt' is the field to filter by. Adjust if needed.
         q = query(q, where('createdAt', '>=', Timestamp.fromDate(startDate)));
      } else if (startDate) {
         console.warn(`ReportService: Invalid startDate provided: ${startDate}.`);
      }

      // *** ADD end date filtering if endDate is provided ***
      if (endDate instanceof Date && isValid(endDate)) {
         //console.log(`ReportService: Applying end date filter <= ${endDate.toISOString()}`);
         // Ensure the end date includes the whole day if needed, or use as is.
         // Firestore '<=' comparison works as expected.
         q = query(q, where('createdAt', '<=', Timestamp.fromDate(endDate)));
      } else if (endDate) {
         console.warn(`ReportService: Invalid endDate provided: ${endDate}.`);
      }

      // Consider adding orderBy if needed, e.g., orderBy('createdAt', 'desc')
      q = query(q, orderBy('createdAt', 'desc')); // Note: Range filters and inequality filters require specific indexes

      const querySnapshot = await getDocs(q);
      const invoices = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      //console.log(`ReportService: Fetched ${invoices.length} invoice records from Firestore for key: ${cacheKey}`);

      // Save the fetched data to cache using reportCacheUtil
      reportCacheUtil.saveToCache(cacheKey, invoices, CACHE_TTL);

      // Ensure dates are Date objects before returning
      return invoices.map(inv => {
         if (inv.createdAt?.seconds) inv.createdAt = new Date(inv.createdAt.seconds * 1000);
         // Add other date conversions as needed
         if (inv.summaryData && Array.isArray(inv.summaryData)) {
            inv.summaryData = inv.summaryData.map(entry => {
               if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
               return entry;
            });
         }
         return inv;
      });

    } catch (error) {
      console.error(`ReportService: Error fetching all invoices for key ${cacheKey}:`, error);
      // Consider specific error handling, e.g., for missing indexes
      if (error.code === 'failed-precondition') {
         console.error("Firestore query failed. This often indicates a missing index. Check the Firestore console for index suggestions based on the query filters (userId, practiceId, createdAt range).");
      }
      throw error; // Re-throw the error
    }
  }

  // --- Fetching All Treatment Data (with Caching) ---
  // Similar caching logic as getAllInvoiceData
  async getAllTreatmentData(userId, practiceId = 'all', startDate = null, endDate = null, options = {}) { // Added endDate param
     if (!userId) {
       console.error("ReportService: getAllTreatmentData requires userId.");
       return [];
     }

     // Construct cache key including endDate
     const cacheKey = `allTreatments_${userId}_${practiceId}_${startDate ? startDate.toISOString() : 'null'}_${endDate ? endDate.toISOString() : 'null'}`;
     const forceRefresh = options?.forceRefresh || false;

     // Try cache unless forceRefresh
     if (!forceRefresh) {
       const cachedData = reportCacheUtil.getFromCache(cacheKey);
       if (cachedData) {
        // console.log(`ReportService: Returning cached treatment data for key: ${cacheKey}`);
         // Ensure dates are Date objects if needed
         return cachedData.map(inv => {
            if (inv.createdAt?.seconds) inv.createdAt = new Date(inv.createdAt.seconds * 1000);
            // Convert summaryData dates
            if (inv.summaryData && Array.isArray(inv.summaryData)) {
               inv.summaryData = inv.summaryData.map(entry => {
                  if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
                  return entry;
               });
            }
            return inv;
         });
       }
     } else {
        //console.log(`ReportService: Force refresh requested for treatment data key: ${cacheKey}`);
     }


     //console.log(`ReportService: Fetching all treatments from Firestore for key: ${cacheKey}`);
     try {
       // Assuming 'invoices' collection holds treatment data within summaryData
       const invoicesRef = collection(db, 'invoices');
       let q = query(invoicesRef, where('userId', '==', userId));

       if (practiceId !== 'all') {
         q = query(q, where('practiceId', '==', practiceId));
       }

       // Add date filtering if startDate is provided
       if (startDate instanceof Date && isValid(startDate)) {
         // console.log(`ReportService: Applying start date filter >= ${startDate.toISOString()} for treatments.`);
          // IMPORTANT: This assumes 'createdAt' exists on the invoice document and is relevant
          // for filtering treatments. If treatments have their own top-level date, adjust this query.
          // If filtering based on dates *within* summaryData is needed, that must happen client-side after fetching.
          console.warn("ReportService: getAllTreatmentData assumes a 'createdAt' field for filtering. Verify data structure.");
          q = query(q, where('createdAt', '>=', Timestamp.fromDate(startDate)));
       } else if (startDate) {
          console.warn(`ReportService: Invalid startDate provided for treatments: ${startDate}.`);
       }

       // *** ADD end date filtering if endDate is provided ***
       if (endDate instanceof Date && isValid(endDate)) {
         // console.log(`ReportService: Applying end date filter <= ${endDate.toISOString()} for treatments.`);
          q = query(q, where('createdAt', '<=', Timestamp.fromDate(endDate)));
       } else if (endDate) {
          console.warn(`ReportService: Invalid endDate provided for treatments: ${endDate}.`);
       }

       // Add default ordering
       q = query(q, orderBy('createdAt', 'desc'));

       const querySnapshot = await getDocs(q);
       // We still fetch the whole invoice document, as treatments are nested
       const treatments = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // console.log(`ReportService: Fetched ${treatments.length} treatment-containing records from Firestore for key: ${cacheKey}`);

       // Save to cache using reportCacheUtil
       reportCacheUtil.saveToCache(cacheKey, treatments, CACHE_TTL);

       // Ensure dates are Date objects before returning
       return treatments.map(inv => {
          if (inv.createdAt?.seconds) inv.createdAt = new Date(inv.createdAt.seconds * 1000);
          // Convert summaryData dates
          if (inv.summaryData && Array.isArray(inv.summaryData)) {
             inv.summaryData = inv.summaryData.map(entry => {
                if (entry.date?.seconds) entry.date = new Date(entry.date.seconds * 1000);
                return entry;
             });
          }
          return inv;
       });

     } catch (error) {
       console.error(`ReportService: Error fetching all treatments for key ${cacheKey}:`, error);
       if (error.code === 'failed-precondition') {
          console.error("Firestore query failed. Check index requirements for treatments query (userId, practiceId, createdAt range).");
       }
       throw error;
     }
  }

  // --- Fetch Oldest Invoice Timestamp ---
  async getOldestInvoiceTimestamp(userId) {
    if (!userId) {
      console.error("ReportService: getOldestInvoiceTimestamp requires userId.");
      return null;
    }

    // Cache key for this specific query
    const cacheKey = `oldestInvoiceTimestamp_${userId}`;

    // Try cache first
    const cachedTimestamp = reportCacheUtil.getFromCache(cacheKey);
    if (cachedTimestamp) {
     // console.log(`ReportService: Returning cached oldest timestamp for user ${userId}`);
      // Convert back to Timestamp object if stored as string/object
      if (cachedTimestamp.seconds) {
        return new Timestamp(cachedTimestamp.seconds, cachedTimestamp.nanoseconds);
      }
      // Handle if stored differently (e.g., ISO string) - adjust as needed
      try {
        return Timestamp.fromDate(new Date(cachedTimestamp));
      } catch {
        return null; // Invalid cached format
      }
    }

   // console.log(`ReportService: Fetching oldest invoice timestamp for user ${userId} from Firestore.`);
    try {
      const invoicesRef = collection(db, 'invoices');
      // Query for the user's invoices, order by createdAt ascending, limit to 1
      const q = query(
        invoicesRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'asc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const oldestInvoice = querySnapshot.docs[0].data();
        const timestamp = oldestInvoice.createdAt; // Assuming 'createdAt' is the Timestamp field
        // Save the timestamp to cache before returning
        // Store as a serializable object or string if Timestamp object isn't directly storable/retrievable
        reportCacheUtil.saveToCache(cacheKey, { seconds: timestamp.seconds, nanoseconds: timestamp.nanoseconds }, CACHE_TTL * 4); // Longer TTL for static data
        return timestamp;
      } else {
        //console.log(`ReportService: No invoices found for user ${userId}.`);
        // Cache the null result to avoid re-querying unnecessarily
        reportCacheUtil.saveToCache(cacheKey, null, CACHE_TTL * 4);
        return null;
      }
    } catch (error) {
      console.error(`ReportService: Error fetching oldest invoice timestamp for user ${userId}:`, error);
      if (error.code === 'failed-precondition') {
         console.error("Firestore query failed. Index missing for oldest timestamp query (userId ASC, createdAt ASC). Check Firestore console.");
      }
      throw error; // Re-throw
    }
  }

  // Helper function used internally by getRevenueBy... functions
  getMonthIndex(monthStr) {
    const monthPart = monthStr?.split(' ')[0] || '';
    const fullMonthMap = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
      'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
    };
    const shortMonthMap = {
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
      'jul': 6, 'aug': 7, 'sep': 8, 'sept': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };
    const lowerMonth = monthPart.toLowerCase();
    if (fullMonthMap[lowerMonth] !== undefined) return fullMonthMap[lowerMonth];
    if (shortMonthMap[lowerMonth] !== undefined) return shortMonthMap[lowerMonth];
    if (!isNaN(parseInt(lowerMonth, 10))) return parseInt(lowerMonth, 10) - 1;
    for (const [month, index] of Object.entries(fullMonthMap)) {
      if (month.startsWith(lowerMonth)) return index;
    }
    console.warn(`Could not determine month index for: "${monthStr}"`);
    return -1; // Return -1 to indicate failure
  }

  // Refined calculateMonthlyAmounts - Process individual treatment entries (matches tax service logic)
  calculateMonthlyAmounts(invoices, viewMode = 'treatmentDate') {
    console.log('calculateMonthlyAmounts called with:', { 
      invoiceCount: invoices.length, 
      viewMode,
      firstInvoiceExample: invoices[0] ? {
        id: invoices[0].id,
        totalAmount: invoices[0].totalAmount,
        summaryDataLength: invoices[0].summaryData?.length
      } : 'none'
    });
    
    const monthlyData = {};
    const processedInvoiceIds = new Set(); // Track processed invoices

    // Helper to process an entry for a specific month
    const processEntry = (monthYear, amount, invoice, entryDate) => {
      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = { month: monthYear, amount: 0, invoices: new Set(), invoiceDetails: [] };
      }
      monthlyData[monthYear].amount += (amount || 0);
      
      // Track unique invoices per month
      if (!monthlyData[monthYear].invoices.has(invoice.id)) {
        monthlyData[monthYear].invoices.add(invoice.id);
        monthlyData[monthYear].invoiceDetails.push({
          id: invoice.id,
          practiceId: invoice.practiceId, 
          practiceName: invoice.practiceDetails?.prac_name || 'Unknown',
          amount: amount, // Use actual entry amount, not total invoice amount
          createdAt: invoice.createdAt,
          status: invoice.status,
          treatmentDate: entryDate
        });
      }
    };

    // Main loop through invoices
    invoices.forEach(invoice => {
      // Skip deleted invoices
      if (invoice.status === 'deleted') {
        return;
      }

      if (viewMode === 'treatmentDate') {
        // Process individual treatment entries (matches tax service logic)
        if (Array.isArray(invoice.summaryData)) {
          invoice.summaryData.forEach(entry => {
            let treatmentDate = null;
            
            // Parse treatment date (same logic as tax service)
            if (entry.date) {
              if (typeof entry.date.toDate === 'function') {
                treatmentDate = entry.date.toDate();
              } else if (entry.date instanceof Date) {
                treatmentDate = entry.date;
              } else {
                treatmentDate = new Date(entry.date);
              }
            }
            
            // Process entry if valid date and amount
            if (treatmentDate && !isNaN(treatmentDate.getTime())) {
              const entryAmount = parseFloat(entry.amount) || 0;
              const monthYear = `${treatmentDate.toLocaleString('default', { month: 'long' })} ${treatmentDate.getFullYear()}`;
              processEntry(monthYear, entryAmount, invoice, treatmentDate);
              processedInvoiceIds.add(invoice.id);
              
              if (invoice.id === invoices[0]?.id) {
                console.log('Processing first invoice entry:', {
                  invoiceId: invoice.id,
                  entryAmount,
                  treatmentDate: treatmentDate.toISOString(),
                  monthYear
                });
              }
            }
          });
        }
      } else { // 'invoiceDate'
        // Use the invoice creation date for entire invoice amount
        let relevantDate = null;
        if (invoice.createdAt?.seconds) {
          relevantDate = new Date(invoice.createdAt.seconds * 1000);
        } else if (invoice.createdAt instanceof Date) {
          relevantDate = invoice.createdAt;
        } else if (typeof invoice.createdAt === 'string') {
           try {
             relevantDate = new Date(invoice.createdAt);
           } catch { relevantDate = null; }
        }
        
        if (relevantDate && !isNaN(relevantDate.getTime())) {
          const amountToAttribute = parseFloat(invoice.totalAmount) || 0;
          const monthYear = `${relevantDate.toLocaleString('default', { month: 'long' })} ${relevantDate.getFullYear()}`;
          processEntry(monthYear, amountToAttribute, invoice, relevantDate);
          processedInvoiceIds.add(invoice.id);
        }
      }
    });

    // Convert Set to array and finalize structure
    const result = Object.values(monthlyData).map(data => ({
      ...data,
      invoices: Array.from(data.invoices) // Convert Set to Array
    }));

    console.log('calculateMonthlyAmounts result:', {
      monthCount: result.length,
      totalAmount: result.reduce((sum, month) => sum + month.amount, 0),
      months: result.map(m => ({ month: m.month, amount: m.amount }))
    });

    return result;
  }

  // --- Get Revenue by Treatment Date (with Caching and Filtering) ---
  async getRevenueByTreatmentDate(userId, months = 'all', practiceId = 'all', startDate = null, endDate = null) {
    try {
      // Fetch all relevant treatment data (uses caching internally)
      const allTreatments = await this.getAllTreatmentData(userId, practiceId, startDate, endDate);
      // Calculate monthly amounts based on treatment dates
      const monthlyResult = this.calculateMonthlyAmounts(allTreatments, 'treatmentDate');

      // Determine if Firestore likely performed the date range filtering
      // Check if BOTH startDate AND endDate were valid Date objects
      const isSpecificDateRange = (startDate instanceof Date && isValid(startDate)) && (endDate instanceof Date && isValid(endDate));
      //console.log(`[getRevenueByTreatmentDate] isSpecificDateRange check: ${isSpecificDateRange} (Start: ${startDate}, End: ${endDate})`);

      // Apply client-side filtering ONLY if it was NOT a specific date range fetch AND months is a number
      if (!isSpecificDateRange && months !== 'all' && typeof months === 'number' && months > 0) {
        const now = new Date();
        // CORRECTED: Use date-fns: Subtract months from the *current* date, then get the start of that month.
        // Example: If now is May 5th 2025 and months = 1 (Last Month):
        // subMonths(now, 1) -> April 5th 2025
        // startOfMonth(...) -> April 1st 2025. This is the first day of the earliest month to include.
        const firstDayOfEarliestMonth = startOfMonth(subMonths(now, months)); // Corrected calculation

        //console.log(`Filtering for last ${months} months. Including months from: ${firstDayOfEarliestMonth.toDateString()}`);

        return monthlyResult.filter(item => {
          // Use date-fns parse for robust parsing of "Month YYYY" string
          let itemMonthDate;
          try {
            // Assuming item.month is like "April 2025"
            itemMonthDate = parse(item.month, 'MMMM yyyy', new Date());
            // Ensure we compare based on the start of the month
            itemMonthDate = startOfMonth(itemMonthDate);
          } catch (e) {
             console.warn(`Could not parse month/year using date-fns: ${item.month}`, e);
             // Fallback parsing (less reliable)
             const monthParts = item.month.split(' ');
             const monthIndex = this.getMonthIndex(monthParts[0]);
             const year = parseInt(monthParts[1]);
             if (!isNaN(year) && monthIndex >= 0 && monthIndex <= 11) {
               itemMonthDate = new Date(year, monthIndex, 1);
               itemMonthDate.setHours(0, 0, 0, 0);
             } else {
               return false; // Exclude unparseable items
             }
          }

          if (isNaN(itemMonthDate.getTime())) {
             console.warn(`Parsed date is invalid for: ${item.month}`);
             return false;
          }

          // Keep the item if its month is on or after the first month to include
          return itemMonthDate >= firstDayOfEarliestMonth;
        });
      }

      // Return all results if months is 'all' or invalid
      return monthlyResult;
    } catch (error) {
      console.error('Error in getRevenueByTreatmentDate:', error);
      throw error;
    }
  }

  // --- Get Revenue by Invoice Date (with Caching and Filtering) ---
  async getRevenueByInvoiceDate(userId, months = 'all', practiceId = 'all', startDate = null, endDate = null) {
    try {
      // Fetch all relevant invoice data (uses caching internally)
      const allInvoices = await this.getAllInvoiceData(userId, practiceId, startDate, endDate);
      // Filter out deleted invoices before calculation
      const activeInvoices = allInvoices.filter(inv => inv.status !== 'deleted');
      // Calculate monthly amounts based on invoice creation dates
      const monthlyResult = this.calculateMonthlyAmounts(activeInvoices, 'invoiceDate');

      // Determine if Firestore likely performed the date range filtering
      const isSpecificDateRange = (startDate instanceof Date && isValid(startDate)) && (endDate instanceof Date && isValid(endDate));
      //console.log(`[getRevenueByInvoiceDate] isSpecificDateRange check: ${isSpecificDateRange} (Start: ${startDate}, End: ${endDate})`);

      // Apply client-side filtering ONLY if it was NOT a specific date range fetch AND months is a number
      if (!isSpecificDateRange && months !== 'all' && typeof months === 'number' && months > 0) {
        const now = new Date();
        // CORRECTED: Use date-fns (same logic as above)
        const firstDayOfEarliestMonth = startOfMonth(subMonths(now, months)); // Corrected calculation

      //  console.log(`Filtering for last ${months} months. Including months from: ${firstDayOfEarliestMonth.toDateString()}`);

        return monthlyResult.filter(item => {
          // Use date-fns parse
          let itemMonthDate;
           try {
             itemMonthDate = parse(item.month, 'MMMM yyyy', new Date());
             itemMonthDate = startOfMonth(itemMonthDate);
           } catch (e) {
              console.warn(`Could not parse month/year using date-fns: ${item.month}`, e);
              // Fallback parsing
              const monthParts = item.month.split(' ');
              const monthIndex = this.getMonthIndex(monthParts[0]);
              const year = parseInt(monthParts[1]);
              if (!isNaN(year) && monthIndex >= 0 && monthIndex <= 11) {
                itemMonthDate = new Date(year, monthIndex, 1);
                itemMonthDate.setHours(0, 0, 0, 0);
              } else {
                return false;
              }
           }

           if (isNaN(itemMonthDate.getTime())) {
              console.warn(`Parsed date is invalid for: ${item.month}`);
              return false;
           }

          // Keep the item if its month is on or after the first month to include
          return itemMonthDate >= firstDayOfEarliestMonth;
        });
      }

      // Return all results if months is 'all' or invalid
      return monthlyResult;
    } catch (error) {
      console.error('Error in getRevenueByInvoiceDate:', error);
      throw error;
    }
  }

  // --- NEW/MISSING: Methods for updating/deleting/restoring (if they belong here) ---
  // These methods now delegate to invoiceService and handle cache invalidation

  // Update payment status: Delegates to invoiceService and handles cache
  async updateInvoicePaymentStatus(invoiceId, isPaid) {
     const userId = getAuth().currentUser?.uid;
     if (!userId) {
       console.error("[ReportService] Cannot update payment status: User not logged in.");
       throw new Error("User not authenticated.");
     }
     if (!invoiceId) {
       console.error("[ReportService] Cannot update payment status: Invoice ID missing.");
       throw new Error("Invoice ID is required.");
     }

     try {
     //  console.log(`[ReportService] Delegating payment status update for ${invoiceId} to invoiceService (isPaid: ${isPaid})`);
       // Delegate the actual Firestore update to invoiceService
       if (isPaid) {
         await invoiceService.markInvoicePaid(userId, invoiceId);
       } else {
         await invoiceService.markInvoiceUnpaid(userId, invoiceId);
       }
      // console.log(`[ReportService] Payment status update successful for ${invoiceId}. Invalidating cache.`);

       // Invalidate cache after successful update
       const cacheKey = `invoice_${userId}_${invoiceId}`;
       reportCacheUtil.removeFromCache(cacheKey); // Invalidate single item
       reportCacheUtil.clearCacheForPrefix(`allInvoices_${userId}`); // Invalidate list caches
       //console.log(`[ReportService] Invalidated cache for invoice ${invoiceId} and related lists.`);

       // Optionally return updated fields if needed, though invoiceService might already return them
       // For consistency, the calling component might refetch or use data returned by invoiceService
       // return { isPaid: isPaid, paidDate: isPaid ? new Date() : null }; // Example return

     } catch (error) {
       console.error(`[ReportService] Error during payment status update delegation for ${invoiceId}:`, error);
       // Re-throw the error so the UI can handle it
       throw error;
     }
  }

  // Delete invoice (soft delete): Delegates to invoiceService and handles cache
  async deleteInvoice(invoiceId) {
     const userId = getAuth().currentUser?.uid;
     if (!userId) {
       console.error("[ReportService] Cannot delete invoice: User not logged in.");
       throw new Error("User not authenticated.");
     }
     if (!invoiceId) {
       console.error("[ReportService] Cannot delete invoice: Invoice ID missing.");
       throw new Error("Invoice ID is required.");
     }

     try {
       //console.log(`[ReportService] Delegating soft delete for ${invoiceId} to invoiceService`);
       // Delegate the actual Firestore update to invoiceService
       // Assuming softDeleteInvoice takes userId and invoiceId
       await invoiceService.softDeleteInvoice(userId, invoiceId);
      // console.log(`[ReportService] Soft delete successful for ${invoiceId}. Invalidating cache.`);

       // Invalidate cache after successful delete
       const cacheKey = `invoice_${userId}_${invoiceId}`;
       reportCacheUtil.removeFromCache(cacheKey);
       reportCacheUtil.clearCacheForPrefix(`allInvoices_${userId}`);
       //console.log(`[ReportService] Invalidated cache for deleted invoice ${invoiceId}.`);

     } catch (error) {
       console.error(`[ReportService] Error during delete delegation for ${invoiceId}:`, error);
       throw error;
     }
  }

  // Restore invoice: Delegates to invoiceService and handles cache
  async restoreInvoice(invoiceId) {
     const userId = getAuth().currentUser?.uid;
     if (!userId) {
       console.error("[ReportService] Cannot restore invoice: User not logged in.");
       throw new Error("User not authenticated.");
     }
     if (!invoiceId) {
       console.error("[ReportService] Cannot restore invoice: Invoice ID missing.");
       throw new Error("Invoice ID is required.");
     }

     try {
      // console.log(`[ReportService] Delegating restore for ${invoiceId} to invoiceService`);
       // Delegate the actual Firestore update to invoiceService
       // Assuming restoreInvoice takes userId and invoiceId
       await invoiceService.restoreInvoice(userId, invoiceId);
     //  console.log(`[ReportService] Restore successful for ${invoiceId}. Invalidating cache.`);

       // Invalidate cache after successful restore
       const cacheKey = `invoice_${userId}_${invoiceId}`;
       reportCacheUtil.removeFromCache(cacheKey);
       reportCacheUtil.clearCacheForPrefix(`allInvoices_${userId}`);
       //console.log(`[ReportService] Invalidated cache for restored invoice ${invoiceId}.`);

     } catch (error) {
       console.error(`[ReportService] Error during restore delegation for ${invoiceId}:`, error);
       throw error;
     }
  }

  // --- Cache Management ---
  // Method to update a specific invoice in the cache
  updateCachedInvoice(userId, invoiceId, updatedFields) {
    const cacheKey = `invoice_${userId}_${invoiceId}`;
    const existingData = reportCacheUtil.getFromCache(cacheKey);
    if (existingData) {
      const newData = { ...existingData, ...updatedFields };
      // Ensure dates remain Date objects if updatedFields contains date strings/timestamps
      if (newData.paidDate && typeof newData.paidDate === 'string') newData.paidDate = new Date(newData.paidDate);
      else if (newData.paidDate?.seconds) newData.paidDate = new Date(newData.paidDate.seconds * 1000);

      reportCacheUtil.saveToCache(cacheKey, newData, CACHE_TTL);
      //console.log(`[ReportService] Updated cache for invoice ${invoiceId}`);

      // Also clear relevant list caches as the update might affect filtering/sorting
      reportCacheUtil.clearCacheForPrefix(`allInvoices_${userId}`);
      //console.log(`[ReportService] Cleared list caches for user ${userId} after single invoice update.`);

    } else {
       ///console.log(`[ReportService] No existing cache entry found for invoice ${invoiceId} to update.`);
       // Optionally, clear list caches anyway if an update happened but wasn't cached
       reportCacheUtil.clearCacheForPrefix(`allInvoices_${userId}`);
    }
  }

  // Method to clear cache (useful on logout or major data changes)
  clearCache(userId) {
    if (userId) {
      reportCacheUtil.clearCacheForPrefix(`invoice_${userId}`);
      reportCacheUtil.clearCacheForPrefix(`allInvoices_${userId}`);
      reportCacheUtil.clearCacheForPrefix(`allTreatments_${userId}`);
      reportCacheUtil.clearCacheForPrefix(`oldestInvoiceTimestamp_${userId}`);
      //console.log(`[ReportService] Cleared all cache entries for user ${userId}`);
    } else {
      // Clear potentially global cache items if any, or log a warning
      console.warn("[ReportService] clearCache called without userId. Clearing only non-user-specific cache if applicable.");
      // Example: reportCacheUtil.clearCacheForPrefix('global_');
    }
  }

} // End of ReportService class

// Ensure all necessary methods are exported
export const reportService = new ReportService();

// Add individual exports if needed elsewhere, though using the instance is typical
// export { getInvoiceById, getAllInvoiceData, ... };